































































  import { Component, Prop } from 'vue-property-decorator'
  import { mixins, Watch } from 'nuxt-property-decorator'
  import Vue from 'vue'
  import { Products } from '@one/types'
  import uuid from 'uuid-random'
  import { EnsureClientSide } from '~/utils/clientSideDecorator'
  import CurrentProductMixin, { ImageVariant, ProductImageSize } from '~/mixins/CurrentProduct'
  import ExternalUrl = Products.ExternalUrl

  interface GalleryOptions {
    titleElement: boolean | string
    youTubeClickToPlay?: boolean
    videoCoverClass?: string
  }

  interface GalleryYoutubeVideo {
    title: string
    type: 'text/html'
    youtube: string
    href: string
    isYoutube: true
    videoThumbnailSrc: string
  }

  interface GalleryImage {
    title: string
    type: 'image/jpeg'
    href: string
    thumbnail: string
    alt: string
  }

  type GalleryItem = GalleryImage | GalleryYoutubeVideo

  @Component({
    components: {
      OneProductTileGallerySwiperCarousel: () =>
        import('~/components/molecules/carousels/OneProductTileGallerySwiperCarousel.vue'),
      OneProductTileGalleryHoverCarousel: () =>
        import('~/components/molecules/carousels/OneProductTileGalleryHoverCarousel.vue'),
    },
  })
  export default class OneGallery extends mixins(CurrentProductMixin) {
    currentImageIndex: number = 0
    lightboxImageIndex: number | null = null
    youtubeLogoImageSrc: string =
      'https://upload.wikimedia.org/wikipedia/commons/thumb/0/09/YouTube_full-color_icon_%282017%29.svg/71px-YouTube_full-color_icon_%282017%29.svg.png'

    placeholderImageSrc: string =
      'https://static-demo-images.s3-eu-west-1.amazonaws.com/photo_plcs_min.svg'

    driftInstance: any | null = null
    thumbnailsMaxHeight: number = 200
    lightboxImagesVariant: ProductImageSize = ProductImageSize.BIG
    uuid: string = uuid()

    @Prop({
      type: String,
      required: true,
    })
    thumbnailsImageVariant!: ProductImageSize

    @Prop({
      type: String,
      required: true,
    })
    mainImageVariant!: ProductImageSize

    @Prop({
      type: String,
      default: '',
    })
    mainImageAlt!: string

    @Prop({
      type: Boolean,
      default: true,
    })
    extended!: boolean

    @Prop({
      type: Boolean,
      required: true,
    })
    zoomEnabled!: boolean

    @Prop({
      type: Number,
      required: false,
      default: 1,
    })
    zoomValue!: number

    @Prop({
      type: Boolean,
      required: true,
    })
    lightboxEnabled!: boolean

    // co z tym gunwem?
    @Prop({
      type: Boolean,
      required: false,
      default: true,
    })
    showIcons!: boolean

    galleryOptions: GalleryOptions = {
      titleElement: false,
      youTubeClickToPlay: false,
      videoCoverClass: 'video-cover toggle',
    }

    @EnsureClientSide
    @Watch('currentImage', { immediate: true })
    async onCurrentImageChanged() {
      await this.$nextTick()
      if (!this.isSmallScreen) {
        const mainImageContainer: Vue = this.$refs.mainImage as Vue
        if (mainImageContainer) {
          this.thumbnailsMaxHeight = mainImageContainer.$el.clientHeight
        }
      }
    }

    get isSmallScreen() {
      return this.extended && ['xs', 'sm', 'md'].includes(this.$mq)
    }

    get smallScreenAndMultipleImages() {
      return this.isSmallScreen && this.imagesAndVideos(this.mainImageVariant)?.length > 1
    }

    get thumbnailsStyles() {
      return {
        'max-height': `${this.thumbnailsMaxHeight}px`,
      }
    }

    mounted() {
      this.initializeZoom()
      this.initializeLightBox()
    }

    isVideo(image: GalleryItem): image is GalleryYoutubeVideo {
      return image.type === 'text/html'
    }

    @EnsureClientSide
    async initializeZoom() {
      if (
        this.zoomEnabled &&
        !this.isSmallScreen &&
        this.$product!.hasPhotos() &&
        this.$mq !== 'xs'
      ) {
        await this.$nextTick()
        const drift = require('drift-zoom').default // https://github.com/imgix/drift/issues/74
        const mainImageContainer: Vue = this.$refs.mainImage as Vue
        mainImageContainer.$el.setAttribute('data-zoom', this.currentImageSrc ?? '')
        // eslint-disable-next-line new-cap
        this.driftInstance = new drift(mainImageContainer.$el, {
          paneContainer: this.$refs.paneContainer,
          containInline: true,
          hoverBoundingBox: true,
          showWhitespaceAtEdges: false,
          injectBaseStyles: true,
          zoomFactor: this.zoomValue,
          inlinePane: 375,
        })
      }
    }

    updateZoom() {
      if (!this.isSmallScreen) {
        const mainImageContainer: Vue = this.$refs.mainImage as Vue
        mainImageContainer.$el.setAttribute('data-zoom', this.currentImageZoom ?? '')
      }
    }

    @EnsureClientSide
    async initializeLightBox() {
      if (this.lightboxEnabled) {
        await this.$nextTick()
      }
    }

    getYoutubeThumbnailImage(url: string) {
      return `https://img.youtube.com/vi/${this.getYoutubeId(url)}/hqdefault.jpg`
    }

    getYoutubeId(url: string) {
      const splittedUrl = url.split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/)
      return splittedUrl[2] !== undefined ? splittedUrl[2].split(/[^0-9a-z_-]/i)[0] : splittedUrl[0]
    }

    updated() {
      if (this.zoomEnabled) {
        this.updateZoom()
      }
    }

    destroyed() {
      if (this.driftInstance) {
        this.driftInstance.destroy()
      }
    }

    selectImage(indexToSet: number) {
      this.currentImageIndex = indexToSet
    }

    onMainImageClick() {
      this.lightboxImageIndex = this.currentImageIndex
    }

    onThumbnailClick(index: number) {
      this.lightboxImageIndex = index
    }

    galleryPlaceholderImage(): GalleryImage {
      const productPlaceholderImage: ExternalUrl = this.$product!.getPlaceholderImage()
      return {
        title: productPlaceholderImage.fileName,
        type: 'image/jpeg',
        href: productPlaceholderImage.url,
        thumbnail: productPlaceholderImage.url,
        alt: productPlaceholderImage?.alt || '',
      }
    }

    get imagesAndVideos() {
      return (imageVariant: ProductImageSize): Array<GalleryItem> => {
        if (this.$product) {
          // @ts-ignore
          return [
            ...this.$product.getPhotosInVariant(imageVariant).map((variant: ImageVariant) => ({
              title: variant.fileName,
              type: 'image/jpeg',
              href: variant.url,
              thumbnail: variant.url,
              alt: variant?.alt || '',
            })),
            ...this.$product.videos.map(video => ({
              title: video.fileName,
              type: 'text/html',
              youtube: this.getYoutubeId(video.url),
              href: video.url,
              videoThumbnailSrc: this.getYoutubeThumbnailImage(video.url),
              isYoutube: true,
            })),
          ]
        }
        return []
      }
    }

    get currentImage(): GalleryItem {
      return (
        this.imagesAndVideos(this.mainImageVariant)[this.currentImageIndex] ||
        this.galleryPlaceholderImage()
      )
    }

    get currentImageSrc(): string {
      if (this.isVideo(this.currentImage)) {
        return this.getYoutubeThumbnailImage(this.currentImage.href)
      }
      return (
        this.$product!.getPhotoInVariant(this.currentImageIndex, this.mainImageVariant) ??
        this.galleryPlaceholderImage().href
      )
    }

    get currentImageZoom(): string {
      if (this.isVideo(this.currentImage)) {
        return this.getYoutubeThumbnailImage(this.currentImage.href)
      }
      return (
        this.$product!.getPhotoInVariant(this.currentImageIndex, ProductImageSize.BIG) ??
        this.galleryPlaceholderImage().href
      )
    }
  }
